"use client";
/**
 * Zwaluw — Voor/Na before-after comparison slider
 *
 * Auto-toggelende voor/na overlay met clip-path animatie. Toont een oude
 * badkamer (voorsituatie-luxury.jpg) die uitveegt naar de nieuwe veilige
 * inloopdouche (nasituatie-luxury.jpg) elke 3,2 seconden.
 *
 * Wordt onder andere gebruikt op /luxury-line-comfortdouche en de homepage.
 * CSS in apps/landing/public/zwaluw/styles-v2.css onder .lx-voorna-*.
 */
import * as React from "react";

export interface BeforeAfterVoorNaProps {
  eyebrow?: string;
  /** Headline lead-tekst (links van em) */
  headline?: string;
  /** Italic-em deel van de headline */
  headlineEm?: string;
  intro?: string;
  /** URL van de voor-situatie foto */
  voorImage?: string;
  /** URL van de na-situatie foto */
  naImage?: string;
  /** Caption-tekst onder voor-tag */
  voorCaption?: string;
  /** Caption-tekst onder na-tag */
  naCaption?: string;
  /** Tekst van de voor-tag (pill linksboven) */
  voorTagLabel?: string;
  /** Tekst van de na-tag (pill rechtsboven) */
  naTagLabel?: string;
  /** Auto-toggle interval in ms (0 = uit). Default 3200. */
  intervalMs?: number;
}

export function BeforeAfterVoorNa({
  eyebrow = "Van bad naar douche",
  headline = "Binnen één dag",
  headlineEm = "veilig douchen.",
  intro = "Zonder grote sloop- of breekwerkzaamheden creëren we een veilige en comfortabele badkamer die klaar is voor de toekomst.",
  voorImage = "/zwaluw/voorsituatie-luxury.jpg",
  naImage = "/zwaluw/nasituatie-luxury.jpg",
  voorCaption = "Een hoge bad­instap.",
  naCaption = "Veilige inloopdouche.",
  voorTagLabel = "Voor",
  naTagLabel = "Na · 1 dag",
  intervalMs = 3200,
}: BeforeAfterVoorNaProps) {
  const [pos, setPos] = React.useState<0 | 100>(0);

  React.useEffect(() => {
    if (intervalMs <= 0) return;
    const initial = window.setTimeout(() => setPos(100), 1200);
    const tick = window.setInterval(() => {
      setPos((p) => (p === 0 ? 100 : 0));
    }, intervalMs);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(tick);
    };
  }, [intervalMs]);

  return (
    <section className="lx-voorna">
      <div className="zw-container">
        <div className="lx-voorna-head">
          <span className="zw-eyebrow">{eyebrow}</span>
          <h2>
            {headline} <em>{headlineEm}</em>
          </h2>
          {intro ? <p>{intro}</p> : null}
        </div>

        <div
          className="lx-voorna-stage"
          data-pos={String(pos)}
          role="img"
          aria-label="Voor en na: van bad naar inloopdouche"
        >
          <div
            className="lx-voorna-layer na"
            style={{ backgroundImage: `url('${naImage}')` }}
          />
          <div
            className="lx-voorna-layer voor"
            style={{
              backgroundImage: `url('${voorImage}')`,
              clipPath: `inset(0 ${pos}% 0 0)`,
            }}
          />
          <div className="lx-voorna-handle" style={{ left: `${100 - pos}%` }} />
          <span className="lx-voorna-tag voor">{voorTagLabel}</span>
          <span className="lx-voorna-tag na">{naTagLabel}</span>
          <div className="lx-voorna-cap voor">{voorCaption}</div>
          <div className="lx-voorna-cap na">{naCaption}</div>
        </div>
      </div>
    </section>
  );
}

export default BeforeAfterVoorNa;
